import React, { useEffect, useState, useCallback } from "react";
import Layout from "../../../layouts/default";
import { IoMdPhonePortrait } from "react-icons/io";
import { Button, Spinner, Alert, Card } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { getSmartConnectSettings } from "../../../store/slices/smartConnect/smartConnectSettings";
import SmartConnectSettingForm from "./SmartConnectSettingForm";
import CreateSmartConnectSettingComponent from "./CreateSmartConnectSetting";
import { hasPermission } from "../../../services/utils";
import { PERMISSIONS } from "../../../contants/permission";

function SmartConnectSettings() {
  const dispatch = useDispatch();

  const [showCreateSettingModal, setShowCreateSettingModal] = useState(false);
  const [selectedSettingId, setSelectedSettingId] = useState(null);

  const {
    data: rawData,
    loading,
    error,
  } = useSelector((state) => state.smartConnectSettingsReducer);

  const settingsList = Array.isArray(rawData)
    ? rawData
    : Array.isArray(rawData?.data)
    ? rawData.data
    : [];

  const fetchSettings = useCallback(() => {
    dispatch(getSmartConnectSettings());
  }, [dispatch]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    if (settingsList.length > 0 && !selectedSettingId) {
      setSelectedSettingId(settingsList[0].id);
    }
  }, [settingsList, selectedSettingId]);

  const selectedSetting = settingsList.find(
    (setting) => setting.id === selectedSettingId,
  );

  const handleSettingCreated = () => {
    fetchSettings();
    setShowCreateSettingModal(false);
  };

  if (!hasPermission(PERMISSIONS.SMART_CONNECT.VIEW)) {
    return (
      <div>
        <h3>You do not have permission to view smart connect settings.</h3>
      </div>
    );
  }

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <IoMdPhonePortrait size={26} />
          <h3 className="mb-0">Smart Connect Configuration</h3>
        </div>

       {
        hasPermission(PERMISSIONS.SMART_CONNECT.CREATE) &&  <Button
          variant="primary"
          onClick={() => setShowCreateSettingModal(true)}
        >
          + Create New Setting
        </Button>
       }
      </div>

      <p className="text-muted mb-4">
        Manage DID assignment and rotation rules for different Smart Connect
        agent groups.
      </p>

      {loading && (
        <div className="text-center my-5">
          <Spinner animation="border" />
        </div>
      )}

      {!loading && (
        <>
          {settingsList.length > 0 && (
            <div
              className="d-flex gap-2 mb-4 pb-2"
              style={{
                overflowX: "auto",
                whiteSpace: "nowrap",
              }}
            >
              {settingsList.map((setting) => (
                <Button
                  key={setting.id}
                  variant={
                    selectedSettingId === setting.id
                      ? "secondary"
                      : "outline-secondary"
                  }
                  onClick={() => setSelectedSettingId(setting.id)}
                  style={{ flexShrink: 0 }}
                >
                  {setting.setting_name}
                </Button>
              ))}
            </div>
          )}

          {settingsList.length > 0 && selectedSetting && (
            <Card className="shadow-sm">
              <Card.Body>
                <SmartConnectSettingForm
                  setting={selectedSetting}
                  onUpdated={fetchSettings}
                />
              </Card.Body>
            </Card>
          )}

          {settingsList.length > 0 && !selectedSetting && (
            <div className="text-muted mt-4">
              Select a setting to view configuration.
            </div>
          )}

          {settingsList.length === 0 && (
            <div className="text-muted mt-4">
              No Smart Connect settings found. Create one to get started.
            </div>
          )}
        </>
      )}

      <CreateSmartConnectSettingComponent
        show={showCreateSettingModal}
        onClose={() => setShowCreateSettingModal(false)}
        onSuccess={handleSettingCreated}
      />
    </Layout>
  );
}

export default SmartConnectSettings;
